import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Lock } from '@mui/icons-material';
import type { Improvement, Incentive } from '@prisma/client';
import dealerOffersService, {
  DealerOfferView,
} from '../api/services/dealerOffers';
import {
  formatUsd,
  IMPROVEMENT_LABELS,
  IMPROVEMENT_VALUES,
  INCENTIVE_LABELS,
  INCENTIVE_VALUES,
  scoreOffers,
} from '../utils/vehicleComparison';
import { timeLeft } from './Deal/DealSummary';

const IMPROVEMENTS = Object.keys(IMPROVEMENT_LABELS) as Improvement[];
const INCENTIVES = Object.keys(INCENTIVE_LABELS) as Incentive[];

const toggle = <T,>(list: T[], item: T) =>
  list.includes(item) ? list.filter((i) => i !== item) : [...list, item];

const DealerOffer = () => {
  const { token = '' } = useParams();
  const [view, setView] = useState<DealerOfferView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [price, setPrice] = useState<string>('');
  const [improvements, setImprovements] = useState<Improvement[]>([]);
  const [incentives, setIncentives] = useState<Incentive[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const v = await dealerOffersService.get(token);
      setView(v);
      setPrice(String(v.price ?? v.vehicle.listPrice ?? ''));
      setImprovements(v.improvements);
      setIncentives(v.incentives);
    } catch (e: any) {
      setError(
        e?.response?.status === 404
          ? 'This offer link is invalid or no longer active.'
          : 'Unable to load this request right now.'
      );
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Where would the price currently typed rank against the revealed competition?
  const preview = useMemo(() => {
    if (!view || !view.competition.revealed) return null;
    const mine = Number(price);
    if (!Number.isFinite(mine) || mine <= 0) return null;
    const scores = scoreOffers([
      {
        id: 'me',
        price: mine,
        mileage: view.vehicle.mileage,
        improvements,
        incentives,
      },
      ...view.competition.offers.map((o, i) => ({
        id: `c${i}`,
        price: o.price as number,
        mileage: o.mileage,
        improvements: o.improvements,
        incentives: o.incentives,
      })),
    ]);
    return { rank: scores.get('me')?.rank ?? 1, total: scores.size };
  }, [view, price, improvements, incentives]);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const v = await dealerOffersService.submit(token, {
        price: Math.round(Number(price)),
        improvements,
        incentives,
      });
      setView(v);
      setSaved(true);
    } catch (e: any) {
      setSubmitError(
        e?.response?.data?.message || 'Could not submit your offer.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  if (!view) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={400} />
      </Container>
    );
  }

  const v = view.vehicle;
  const expires = new Date(view.deal.offerExpDate);
  const sealedUntil = new Date(view.deal.initialWindowEnd);
  const accepted = view.status === 'ACCEPTED';
  const declined = view.status === 'REJECTED';
  const canSubmit = view.deal.canSubmit;
  const bump = (delta: number) =>
    setPrice(String(Math.max(0, Math.round(Number(price || 0) + delta))));

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              Price request for {view.dealer}
            </Typography>
            <Typography variant="h5" fontWeight={600}>
              {v.year} {v.make} {v.model} {v.trim}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              VIN {v.vin} · {v.mileage.toLocaleString()} miles · listed at{' '}
              {formatUsd(v.listPrice)}
            </Typography>
          </Box>
          <Box textAlign={{ xs: 'left', sm: 'right' }}>
            {accepted ? (
              <Chip color="success" label="Accepted by buyer" />
            ) : declined ? (
              <Chip label="Declined by buyer" />
            ) : view.deal.status === 'ACCEPTED' ? (
              <Chip label="Buyer chose another offer" />
            ) : (
              <>
                <Chip
                  color={canSubmit ? 'primary' : 'default'}
                  label={canSubmit ? 'Open' : 'Closed'}
                />
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {canSubmit
                    ? `${timeLeft(expires)} left`
                    : 'The offer window has closed'}
                </Typography>
              </>
            )}
          </Box>
        </Stack>

        {v.photos?.[0] && (
          <Box
            sx={{
              mb: 3,
              width: '100%',
              height: 260,
              overflow: 'hidden',
              borderRadius: 2,
            }}
          >
            <img
              src={v.photos[0]}
              alt={`${v.year} ${v.make} ${v.model}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        )}

        {accepted && view.buyer && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography fontWeight={600}>
              The buyer accepted your {formatUsd(view.price)} offer.
            </Typography>
            <Typography>
              {view.buyer.firstName} {view.buyer.lastName}
              {view.buyer.email && ` · ${view.buyer.email}`}
              {view.buyer.phone && ` · ${view.buyer.phone}`}
            </Typography>
          </Alert>
        )}

        {canSubmit && (
          <>
            <Typography variant="h6" gutterBottom>
              {view.price ? 'Revise your offer' : 'Make your offer'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Quote your best out-the-door price for this exact vehicle.
              Reconditioning and service extras are credited toward the
              buyer&apos;s comparison at the dollar values shown.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
                  <Stack spacing={0.5}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => bump(-1000)}
                    >
                      −$1,000
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => bump(-500)}
                    >
                      −$500
                    </Button>
                  </Stack>
                  <TextField
                    fullWidth
                    label="Out-the-door price"
                    value={price}
                    onChange={(e) =>
                      setPrice(e.target.value.replace(/[^0-9]/g, ''))
                    }
                    inputProps={{ inputMode: 'numeric' }}
                    InputProps={{
                      startAdornment: (
                        <Typography color="textSecondary" sx={{ mr: 0.5 }}>
                          $
                        </Typography>
                      ),
                    }}
                  />
                  <Stack spacing={0.5}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => bump(500)}
                    >
                      +$500
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => bump(1000)}
                    >
                      +$1,000
                    </Button>
                  </Stack>
                </Box>
                {v.listPrice && Number(price) > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {Number(price) <= v.listPrice
                      ? `${formatUsd(
                          v.listPrice - Number(price)
                        )} below your listed price`
                      : `${formatUsd(
                          Number(price) - v.listPrice
                        )} above your listed price`}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={5}>
                {preview ? (
                  <Alert
                    severity={preview.rank === 1 ? 'success' : 'info'}
                    icon={false}
                  >
                    <Typography fontWeight={600}>
                      This offer would rank #{preview.rank} of {preview.total}
                    </Typography>
                    <Typography variant="body2">
                      Ranking weighs price, mileage ($0.15/mile), and included
                      extras.
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="info" icon={<Lock fontSize="small" />}>
                    Sealed bidding until{' '}
                    {sealedUntil.toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                    .
                    {view.competition.total > 0 &&
                      ` ${view.competition.total} other dealer${
                        view.competition.total === 1 ? ' was' : 's were'
                      } asked.`}
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Reconditioning included
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {IMPROVEMENTS.map((imp) => (
                    <Chip
                      key={imp}
                      label={`${IMPROVEMENT_LABELS[imp]} · +${formatUsd(
                        IMPROVEMENT_VALUES[imp]
                      )}`}
                      color="primary"
                      variant={
                        improvements.includes(imp) ? 'filled' : 'outlined'
                      }
                      onClick={() =>
                        setImprovements((prev) => toggle(prev, imp))
                      }
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Service extras
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {INCENTIVES.map((inc) => (
                    <Chip
                      key={inc}
                      label={`${INCENTIVE_LABELS[inc]} · +${formatUsd(
                        INCENTIVE_VALUES[inc]
                      )}`}
                      color="secondary"
                      variant={incentives.includes(inc) ? 'filled' : 'outlined'}
                      onClick={() => setIncentives((prev) => toggle(prev, inc))}
                    />
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12}>
                {submitError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {submitError}
                  </Alert>
                )}
                {saved && !submitError && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Offer saved. The buyer has been notified. You can revise it
                    any time before the window closes.
                  </Alert>
                )}
                <LoadingButton
                  variant="contained"
                  size="large"
                  loading={submitting}
                  disabled={!Number(price)}
                  onClick={submit}
                >
                  {view.price ? 'Update offer' : 'Submit offer'}
                </LoadingButton>
              </Grid>
            </Grid>
          </>
        )}

        {!canSubmit && !accepted && view.price && (
          <Typography>Your final offer was {formatUsd(view.price)}.</Typography>
        )}
      </Paper>

      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">Competing offers</Typography>
          <Typography variant="body2" color="text.secondary">
            {view.competition.priced} of {view.competition.total} dealers have
            responded
          </Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {!view.competition.revealed ? (
          <Alert severity="info">
            Competing offers are hidden until the sealed-bid period ends. Submit
            your best price now — you&apos;ll be able to revise it once the
            competition is visible.
          </Alert>
        ) : view.competition.offers.length === 0 ? (
          <Typography color="text.secondary">
            No other dealer has submitted a price yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vehicle</TableCell>
                <TableCell align="right">Miles</TableCell>
                <TableCell align="right">Listed</TableCell>
                <TableCell align="right">Offer</TableCell>
                <TableCell>Extras</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...view.competition.offers]
                .sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
                .map((o, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      {o.year} {v.make} {v.model} {o.trim}
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Dealer identity hidden
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {o.mileage.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {formatUsd(o.listPrice)}
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatUsd(o.price)}</strong>
                    </TableCell>
                    <TableCell>
                      {[
                        ...o.improvements.map((x) => IMPROVEMENT_LABELS[x]),
                        ...o.incentives.map((x) => INCENTIVE_LABELS[x]),
                      ].join(', ') || '—'}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center">
            <Lock fontSize="small" />
            <Typography variant="subtitle1">About this buyer</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            This is a real shopper comparing specific listings, not a form-fill
            lead. Their name and contact details are shared with you only if
            they accept your offer.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DealerOffer;
